import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  DirectionsRenderer,
  OverlayView,
} from "@react-google-maps/api";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardHeader from "../../components/header/DashboardHeader";
import Pagination from "../../components/pagination/Pagination";
import {
  Grid,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Box,
  Typography
  , Divider
} from "@mui/material";
import BusIcon from "../../images/bus-svgrepo-com.svg";
// import BusIcon from "../../images/vehicle/Map_Vehicle_active.svg";
import CircularProgress from "@mui/material/CircularProgress";
import config from "../../config.json";
import "./stops.css";
import NavMenu from "../../components/navbar/NavMenu";
import TitleBar from "../../components/navbar/TitleBar";
import { useTranslation } from "react-i18next";
import SubTitle from "../../components/common/SubTitle";
import MonitorSubtitle from "../../components/common/MonitorSubtitle";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#647490",
    color: "white",
    fontSize: 20,
    fontWeight: 600,
    width: "20%",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 24,
  },
}));

const colors = [
  "#20A8E0",
  "#8FBC8F",
  "#FFD700",
  "#6495ED",
  "#FF69B4",
  "#FFA07A",
  "#ADFF2F",
  "#FF4500",
  "#9370DB",
  "#FFFFFF",
];

const libraries = ["places", "directions", "geometry"];

const LoadingScreen = () => (
  <>
    <TitleBar />
    <NavMenu />
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <div>Loading map data...</div>
    </div>
  </>
);

const AdminStopView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedStopData, localStopsData } = location.state || {};
  const [stopData, setStopData] = useState(null);
  const [allStopData, setAllStopData] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [directions, setDirections] = useState([]);
  const [vehicleData, setVehicleData] = useState({});
  const [error, setError] = useState(null);
  const routePathsRef = useRef({});
  const [map, setMap] = useState(null);
  const [labelPositions, setLabelPositions] = useState({});
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 768);
  const [vehicleHistory, setVehicleHistory] = useState({});
  const [stoppedVehicles, setStoppedVehicles] = useState({});
  const [vehicleIds, setVehicleIds] = useState([]);
  const [userState, setUserState] = useState(null);
  const [hasMapPermission, setHasMapPermission] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isConnectedRef = useRef(false);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  // Helper functions
  const normalizeDeviceData = (data) => {
    if (data.deviceId && data.latitude && data.longitude) return data;
    return {
      deviceId: data.device_id,
      latitude: data.Latitude,
      longitude: data.Longitude,
      timestamp: new Date().toISOString(),
      rawData: data.RawData || "",
    };
  };

  const getBackgroundColor = (deviceId) => {
    const index = Math.abs(hashString(deviceId)) % colors.length;
    return colors[index];
  };

  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    return hash;
  };

  const calculateHeading = (prev, current) => {
    if (!prev || !current) return null;
    return window.google.maps.geometry.spherical.computeHeading(
      new window.google.maps.LatLng(prev.lat, prev.lng),
      new window.google.maps.LatLng(current.lat, current.lng)
    );
  };

  const snapToRoute = useCallback(
    (routeId, position) => {
      const routePath = routePathsRef.current[routeId];
      if (!routePath) return position;
      const point = new window.google.maps.LatLng(position.lat, position.lng);
      let closestPoint = point;
      let minDistance = Infinity;
      for (let i = 0; i < routePath.length - 1; i++) {
        const start = routePath[i];
        const end = routePath[i + 1];
        const projectedPoint = window.google.maps.geometry.spherical.interpolate(
          start,
          end,
          0.5
        );
        const distance =
          window.google.maps.geometry.spherical.computeDistanceBetween(
            point,
            projectedPoint
          );
        if (distance < minDistance) {
          closestPoint = projectedPoint;
          minDistance = distance;
        }
      }
      return { lat: closestPoint.lat(), lng: closestPoint.lng() };
    },
    []
  );

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds) || seconds <= 0) return "0";
    if (seconds < 60) return `${seconds} sec`;
    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} min`;
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} hr ${minutes > 0 ? `${minutes} min` : ""}`;
  };

  // Fetch user permissions
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userState"));
    if (user) {
      setUserState(user);
      const mapPermission = user.features.some(
        (feature) =>
          feature.api_path === "/api/stop/organization" && feature.action === "GET"
      );
      setHasMapPermission(mapPermission);
    }
  }, []);

  // Set stop data and vehicle IDs on initial load
  useEffect(() => {
    const locationStopData = location.state?.selectedStopData;
    const allStop = location.state?.localStopsData;
    if (!locationStopData) {
      setError("No stop data available");
      return;
    }
    setStopData(locationStopData);
    setAllStopData(allStop);
    const deviceIds = locationStopData.routes
      .flatMap((route) => route.vehicles)
      .map((vehicle) => vehicle.device_id);
    setVehicleIds(deviceIds); // Set once on mount
  }, [location.state]);

  // Manage SSE connection
  useEffect(() => {
    if (vehicleIds.length === 0) {
      console.log("No vehicle IDs, skipping SSE connection");
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        isConnectedRef.current = false;
      }
      return;
    }

    const connectSSE = () => {
      if (isConnectedRef.current) {
        console.log("Already connected, skipping new connection");
        return;
      }

      const query = vehicleIds.map((id) => `vehicleId=${id}`).join("&");
      const sseUrl = `${config.baseURL}/redis/stream?${query}`;
      console.log(`Attempting to connect to SSE URL: ${sseUrl}`);

      if (eventSourceRef.current) {
        console.log("Closing previous SSE connection");
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      const source = new EventSource(sseUrl);
      eventSourceRef.current = source;

      source.onopen = () => {
        console.log("SSE connection opened successfully");
        isConnectedRef.current = true;
      };

      source.onerror = (error) => {
        console.error("SSE connection error:", error);
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        isConnectedRef.current = false;
        if (!reconnectTimeoutRef.current) {
          console.log("Scheduling reconnection in 5 seconds");
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log("Reconnecting SSE...");
            connectSSE();
          }, 5000);
        }
      };

      source.onmessage = (event) => {
        try {
          console.log("Received SSE message:", event.data);
          const data = JSON.parse(event.data);
          handleSSEMessage(data);
        } catch (e) {
          console.error("Error parsing SSE message:", e);
        }
      };
    };

    connectSSE();

    return () => {
      console.log("Component unmounting, cleaning up SSE");
      if (eventSourceRef.current) {
        console.log("Closing SSE connection");
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        console.log("Clearing reconnect timeout");
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      isConnectedRef.current = false;
    };
  }, [vehicleIds]); // Reconnect only if vehicleIds change

  // Fetch directions
  useEffect(() => {
    if (stopData && isLoaded) {
      fetchDirections(stopData.routes);
    }
  }, [stopData, isLoaded]);

  const fetchDirections = async (routes) => {
    if (!routes?.length) {
      setError("No routes available for this stop");
      return;
    }
    const directionsService = new window.google.maps.DirectionsService();
    const directionsPromises = routes.map((route) =>
      new Promise((resolve, reject) => {
        const waypoints = route.routeCoordinates.slice(1, -1).map((coord) => ({
          location: new window.google.maps.LatLng(
            parseFloat(coord.latitude),
            parseFloat(coord.longitude)
          ),
          stopover: true,
        }));
        directionsService.route(
          {
            origin: new window.google.maps.LatLng(
              parseFloat(route.routeCoordinates[0].latitude),
              parseFloat(route.routeCoordinates[0].longitude)
            ),
            destination: new window.google.maps.LatLng(
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1].latitude
              ),
              parseFloat(
                route.routeCoordinates[route.routeCoordinates.length - 1].longitude
              )
            ),
            waypoints,
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (result, status) => {
            if (status === window.google.maps.DirectionsStatus.OK) {
              routePathsRef.current[route.id] = result.routes[0].overview_path;
              resolve(result);
            } else {
              reject(`Error fetching directions: ${status}`);
            }
          }
        );
      })
    );
    try {
      const results = await Promise.all(directionsPromises);
      setDirections(results);
      setIsInitialized(true);
    } catch (error) {
      console.error("Error fetching directions:", error);
      setError("Failed to fetch route directions");
    }
  };

  // Handle SSE messages
  const handleSSEMessage = useCallback(
    async (data) => {
      if (data.type && data.type === "connected") {
        console.log("SSE connected:", data);
        return;
      }

      if (data.device_id && data.Latitude && data.Longitude) {
        const normalizedData = normalizeDeviceData(data);
        const deviceId = normalizedData.deviceId;
        const position = {
          lat: parseFloat(normalizedData.latitude),
          lng: parseFloat(normalizedData.longitude),
        };

        const route = stopData.routes.find((r) =>
          r.vehicles.some((v) => v.device_id === deviceId)
        );
        if (!route) {
          console.warn(`No route found for vehicle ${deviceId}`);
          return;
        }

        const newHistory = { ...vehicleHistory };
        const prevPosition = vehicleHistory[deviceId]?.position;
        const currentTime = Date.now();
        const lastUpdateTime = vehicleHistory[deviceId]?.timestamp || 0;
        const timeDiff = currentTime - lastUpdateTime;

        const newStoppedVehicles = { ...stoppedVehicles };
        if (prevPosition) {
          const distance =
            window.google.maps.geometry.spherical.computeDistanceBetween(
              new window.google.maps.LatLng(prevPosition.lat, prevPosition.lng),
              new window.google.maps.LatLng(position.lat, position.lng)
            );
          newStoppedVehicles[deviceId] = distance < 1 && timeDiff > 30000;
        }

        newHistory[deviceId] = { position, timestamp: currentTime };

        let disTimeData = { distance: 0, eta: 0 };
        try {
          const isExtreme =
            route.routeCoordinates[0].stopId === stopData.stopId ||
            route.routeCoordinates[route.routeCoordinates.length - 1].stopId ===
            stopData.stopId;
          const disTimeRes = await fetch(`${config.baseURL}/distance/time/stop`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              vehicleId: deviceId,
              longitude: stopData.longitude,
              latitude: stopData.latitude,
              isExtremePoint: isExtreme,
            }),
          });
          disTimeData = await disTimeRes.json();
        } catch (error) {
          console.error(`Error fetching distance/ETA for ${deviceId}:`, error);
        }

        const snappedPosition = snapToRoute(route.id, position);

        setVehicleData((prev) => ({
          ...prev,
          [deviceId]: {
            position: snappedPosition,
            routeId: route.routeId,
            speed: parseFloat(normalizedData.speed || 0),
            distance: disTimeData.distance,
            eta: disTimeData.eta,
            heading: calculateHeading(prevPosition, snappedPosition),
          },
        }));

        setVehicleHistory(newHistory);
        setStoppedVehicles(newStoppedVehicles);
      } else {
        console.warn("Invalid SSE data:", data);
      }
    },
    [stopData, vehicleHistory, stoppedVehicles, snapToRoute, calculateHeading]
  );

  // Handle screen resize
  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth > 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const mapStyles = [
    { featureType: "all", elementType: "all", stylers: [{ visibility: "off" }] },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ visibility: "on" }, { color: "black" }],
    },
  ];

  const mapContainerStyle = {
    width: "100%",
    height: "550px",
    border: "1px solid #000000",
    marginTop: "20px",
    borderRadius: "20px"
  };

  console.log(stopData, "hereeeee")


  const renderMap = () => {
    const center = {
      lat: parseFloat(stopData.latitude),
      lng: parseFloat(stopData.longitude),
    };
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(center);
    allStopData?.forEach((stop) => {
      bounds.extend({
        lat: parseFloat(stop.latitude),
        lng: parseFloat(stop.longitude),
      });
    });
    return (
      <Box
        sx={{
          borderRadius: "10px",
          padding: { xs: "10px", sm: "15px", md: "20px" },
          backgroundColor: "#FFFFFF",
          border: "1px solid #A5DCF3",
          margin: { xs: "10px 15px", sm: "10px 20px", md: "20px 40px" }, // Responsive margins
          boxShadow: "-2px 1px 5px 1px #00000040",
          maxWidth: "100%"
        }}
      >
        <Box
          sx={{
            minHeight: "48px",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: { xs: "center", sm: "left" },
            alignItems: { xs: "flex-start", sm: "flex-start" },
            gap: { xs: 1, sm: 0 },
            padding: { xs: "8px 12px", sm: "10px 14px", md: "12px 16px" },
            background: "#E8EAEE",
            boxShadow: "0px 2px 5px 0px #00000040"
          }}
        >
          <Box sx={{
            display: "flex",
            alignItems: "center",
            width: { xs: "100%", sm: "100%" }
          }}>
            <Typography
              sx={{
                color: "#414958",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%"
              }}
            >
              Longitude:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 600,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                color: "#183059",
                letterSpacing: "0%",
                ml: 1,
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {stopData?.longitude || "---"}
            </Typography>
          </Box>

          <Box sx={{
            display: "flex",
            alignItems: "center",
            width: { xs: "100%", sm: "100%" }
          }}>
            <Typography
              sx={{
                color: "#414958",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                letterSpacing: "0%"
              }}
            >
              Latitude:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 600,
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                lineHeight: "100%",
                color: "#183059",
                letterSpacing: "0%",
                ml: 1,
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {stopData.latitude || "---"}
            </Typography>
          </Box>
        </Box>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          onLoad={(map) => {
            setMap(map);
            map.fitBounds(bounds, { padding: { top: 50, right: 50, bottom: 50, left: 50 } });
            setTimeout(() => map.setCenter(center), 100);
          }}
          onUnmount={onUnmount}
          options={{
            styles: mapStyles,
            zoom: 16.5,
            minZoom: 3,
            maxZoom: 20,
            zoomControl: true,
          }}
        >
          {allStopData &&
            allStopData.map((stop) => {
              const isSelected = stop.stopId === stopData.stopId;
              return (
                <React.Fragment key={stop.id}>
                  <OverlayView
                    position={{
                      lat: parseFloat(stop.latitude),
                      lng: parseFloat(stop.longitude),
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div
                      style={{
                        position: "relative",
                        top: isSelected ? "-69px" : "-62px",
                        left: "0px",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: "50%",
                          transform: "translateX(-50%)",
                          backgroundColor: "white",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          color: isSelected ? "blue" : "black",
                          fontWeight: isSelected ? 600 : 400,
                          fontSize: isSelected ? "16px" : "10px",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                          whiteSpace: "nowrap",
                          zIndex: 1000,
                        }}
                      >
                        {stop.name}
                      </div>
                    </div>
                  </OverlayView>
                  <Marker
                    key={stop.stopId}
                    position={{
                      lat: parseFloat(stop.latitude),
                      lng: parseFloat(stop.longitude),
                    }}
                    title={stop.name}
                    icon={{
                      url: isSelected
                        ? "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                        : undefined,
                      scaledSize: new window.google.maps.Size(
                        isSelected ? 40 : 20,
                        isSelected ? 40 : 20
                      ),
                    }}
                  />
                </React.Fragment>
              );
            })}
          {directions.map((direction, index) => (
            <DirectionsRenderer
              key={index}
              directions={direction}
              options={{
                suppressMarkers: true,
                polylineOptions: {
                  strokeColor: "#FF0000",
                  strokeOpacity: 0.8,
                  strokeWeight: 3,
                },
              }}
            />
          ))}
          {Object.entries(vehicleData).map(([deviceId, data]) => (
            <React.Fragment key={deviceId}>
              <OverlayView
                position={data.position}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  style={{
                    position: "relative",
                    top: "-96px",
                    left: "0px",
                    width: "2px",
                    height: "2px",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      width: "2px",
                      height: "60px",
                      backgroundColor: getBackgroundColor(deviceId),
                      left: "50%",
                      top: "30px",
                      transform: "translateX(-50%)",
                      zIndex: 2,
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: `${-40 + (labelPositions[deviceId] || 0)}px`,
                      left: "50%",
                      transform: "translateX(-50%)",
                      color: "rgb(0, 0, 0)",
                      fontSize: "10px",
                      fontWeight: 600,
                      fontFamily: "Arial, sans-serif",
                      lineHeight: "14.06px",
                      padding: "8px",
                      textAlign: "left",
                      whiteSpace: "nowrap",
                      backgroundColor: getBackgroundColor(deviceId),
                      borderRadius: "12px",
                      zIndex: 1000,
                      border: "1px solid black",
                    }}
                  >
                    {deviceId}
                  </div>
                </div>
              </OverlayView>
              <Marker
                key={deviceId}
                position={data.position}
                title={`Vehicle ${deviceId}`}
                icon={{
                  url: BusIcon,
                  scaledSize: new window.google.maps.Size(30, 30),
                  anchor: new window.google.maps.Point(15, 15),
                }}
              />
            </React.Fragment>
          ))}
        </GoogleMap>
      </Box>

    );
  };

  const renderTable = () => {
    const sortedVehicles = Object.entries(vehicleData).sort((a, b) => {
      const distA = parseFloat(a[1].distance);
      const distB = parseFloat(b[1].distance);
      if (isNaN(distA)) return 1;
      if (isNaN(distB)) return -1;
      return distA - distB;
    });
    return (
      <>
        {/* <div
          className="form_header"
          style={{
            marginBottom: "0px",
            height: "50px",
            borderRadius: "8px",
            backgroundColor: "#647490",
            fontSize: "28px",
            padding: "0px 24px",
          }}
        >
          {t("Vehicles on this Stop")}
        </div> */}
        <Box sx={{
          overflowX: "auto",
          borderRadius: "10px",
          padding: "20px",
          backgroundColor: "#FFFFFF",
          border: "1px solid #A5DCF3",
          margin: { xs: "10px 15px", sm: "10px 20px", md: "10px 40px" }, // Responsive margins
          boxShadow: "-2px 1px 5px 1px #00000040"
        }}>
          <Table style={{ width: "100%", marginTop: "20px", borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow style={{ backgroundColor: "#647490", borderRadius: "8px" }}>
                <StyledTableCell style={{
                  padding: "8px",
                  textAlign: "left",
                  paddingLeft: "24px",
                  borderTopLeftRadius: "8px",
                  borderBottomLeftRadius: "8px"
                }}>
                  {t("Vehicle")}
                </StyledTableCell>
                <StyledTableCell style={{
                  padding: "8px",
                  textAlign: "left"
                }}>
                  {t("Route")}
                </StyledTableCell>
                <StyledTableCell style={{
                  padding: "8px",
                  textAlign: "left"
                }}>
                  {t("Distance")}
                </StyledTableCell>
                <StyledTableCell style={{
                  padding: "8px",
                  textAlign: "left"
                }}>
                  {t("ETA")}
                </StyledTableCell>
                <StyledTableCell style={{
                  textAlign: "right",
                  padding: "8px",
                  paddingRight: "24px",
                  borderTopRightRadius: "8px",
                  borderBottomRightRadius: "8px"
                }}>
                  {t("Speed")}(km/h)
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedVehicles.length > 0 ? (
                sortedVehicles.map(([deviceId, data]) => (
                  <TableRow key={deviceId}>
                    <TableCell style={{ paddingLeft: "24px", fontWeight: "400", fontSize: "16px" }}>
                      {deviceId}
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>{data.routeId}</TableCell>
                    <TableCell style={{ padding: "8px" }}>
                      {parseFloat(data.distance).toFixed(1)} km
                    </TableCell>
                    <TableCell style={{ padding: "8px" }}>{formatTime(data.eta)}</TableCell>
                    <TableCell style={{ textAlign: "center" }}>{data.speed} km/h</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} style={{ textAlign: "center" }}>
                    {t("No vehicles currently available")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </>
    );
  };

  const renderRouteTable = () => (
    <>
      <div
        style={{
          paddingTop: "32px",
          display: "flex",
          marginBottom: "32px",
          flexDirection: isLargeScreen ? "row" : "column",
        }}
      >
        <div style={{ flex: 3 }}>{renderStopDetails()}</div>
        <div style={{ flex: 7, marginRight: "20px" }}>
          <div style={{ flex: 1, marginRight: "20px" }}>
            <div
              className="form_header form_title"
              style={{
                marginBottom: "0px",
                height: "80px",
                borderRadius: "8px",
                backgroundColor: "#647490",
                fontSize: "28px",
              }}
            >
              {t("Assigned Routes")}
            </div>
            <div>
              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "-20px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f2f2f2" }}>
                    <th
                      style={{
                        padding: "24px",
                        textAlign: "left",
                        background: "rgba(32, 168, 224, 1)",
                        color: "white",
                        fontWeight: "600",
                      }}
                    >
                      {t("Route ID")}
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        background: "rgba(32, 168, 224, 1)",
                        textAlign: "right",
                        color: "white",
                        paddingRight: "24px",
                        fontWeight: "600",
                      }}
                    >
                      {t("Route Name")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stopData.routes.map((route) => (
                    <tr key={route.routeId}>
                      <td style={{ padding: "8px", paddingLeft: "24px" }}>{route.routeId}</td>
                      <td style={{ paddingRight: "20px", textAlign: "right" }}>{route.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              itemsPerPage={10}
              currentPage={1}
              onPageChange={(page) => console.log(page)}
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderStopDetails = () => (
    <div style={{ margin: "0px 24px" }}>
      <div
        className="form_header form_title"
        style={{
          marginBottom: "0px",
          height: "50px",
          borderRadius: "8px",
          backgroundColor: "#647490",
          fontSize: "28px",
        }}
      >
        {t("Stop Details")}
      </div>
      <div style={{ marginTop: "32px", lineHeight: "40px" }}>
        <span style={{ fontWeight: "600" }}>{t("Stop ID")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.stopId}</span>
        <br />
        <span style={{ fontWeight: "600" }}>{t("Total Routes")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.routes.length}</span>
        <br />
        <span style={{ fontWeight: "600" }}>{t("Stop Name")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.name}</span>
        <br />
        <span style={{ fontWeight: "600" }}>{t("Longitude")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.longitude}</span>
        <br />
        <span style={{ fontWeight: "600" }}>{t("Latitude")}:</span>
        <span style={{ fontWeight: "400" }}> {stopData.latitude}</span>
      </div>
      <div
        style={{
          display: "flex",
          gap: "2px",
          flexDirection: isLargeScreen ? "row" : "column",
        }}
      >
        <div
          className="customButton_add"
          style={{
            background: "#F9E719",
            color: "black",
            fontSize: "19px",
            whiteSpace: isLargeScreen ? "nowrap" : "normal",
          }}
        >
          {t("View Stop Schedules")}
        </div>
        <div
          className="customButton_add"
          onClick={() =>
            navigate(`/track/screen/${selectedStopData.id}`, {
              state: { selectedStopData, localStopsData },
            })
          }
          style={{
            marginLeft: isLargeScreen ? "10px" : "0px",
            background: "rgba(32, 168, 224, 1)",
            color: "white",
            whiteSpace: isLargeScreen ? "nowrap" : "normal",
            cursor: "pointer",
          }}
        >
          {t("Stop View")}
        </div>
      </div>
    </div>
  );

  if (loadError) {
    return (
      <>
        <TitleBar />
        <NavMenu />
        <div>Map cannot be loaded right now, sorry.</div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <TitleBar />
        <NavMenu />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            textAlign: "center",
          }}
        >
          <p>{error}</p>
        </div>
      </>
    );
  }
  if (!isLoaded || !stopData || !isInitialized) {
    return <LoadingScreen />;
  }



  const handleViewStop = () => {
    navigate(`/track/screen/${selectedStopData.id}`, {
      state: { selectedStopData, localStopsData },
    })
  }
  const handleStopSchedules = () => {
    navigate("/Stops/Schedule/Details", {
      state: { id: selectedStopData.id }
    });
  }
  // console.log(selectedStopData)
  // return (
  //   <>
  //     <TitleBar />
  //     <NavMenu />
  //     <SubTitle
  //       title={t("Monitor Stop")}
  //       path={t("Stops > Monitor Stop")}
  //       track={"Stop View"}
  //       add={"Stop Schedules"}
  //       onTrackClick={handleViewStop} />
  //     <div style={{ padding: "0px 20px", height: "100vh" }}>
  //       {renderRouteTable()}
  //       {hasMapPermission && renderMap()}
  //       {renderTable()}
  //     </div>
  //   </>
  // );
  return (
    <>
      <TitleBar />
      <NavMenu />
      <MonitorSubtitle
        title={t("Monitor Stop")}
        path={t("Stops > Monitor Stop")}
        track={"Stop View"}
        add={"Stop Schedules"}
        onTrackClick={handleViewStop}
        onAddClick={handleStopSchedules} />

      <Box
        sx={{
          borderRadius: "10px",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // Stack vertically on small screens
          justifyContent: "space-between",
          padding: { xs: "15px", sm: "15px 30px", md: "20px 60px" }, // Responsive padding
          backgroundColor: "#FFFFFF",
          border: "1px solid #A5DCF3",
          margin: { xs: "0px 15px", sm: "0px 20px", md: "0px 40px" }, // Responsive margins
          boxShadow: "-2px 1px 5px 1px #00000040",
          gap: { xs: 2, sm: 0 } // Add gap between items on small screens
        }}
      >
        {/* Stop Name Section */}
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
          mb: { xs: 2, sm: 0 } // Bottom margin only on small screens
        }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059"
              }}
            >
              Stop Name
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "18px", md: "24px" }, // Responsive font size
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%"
            }}
          >
            {stopData.name}
          </Typography>
        </Box>

        {/* Divider - horizontal on small screens, vertical on larger */}
        <Divider
          orientation={{ xs: "horizontal", sm: "vertical" }}
          flexItem
          sx={{
            mx: { sm: 2 },
            my: { xs: 2, sm: 0 }, // Vertical margin on small screens
            height: { xs: "1px", sm: "60px" }, // Height for horizontal vs vertical
            width: { xs: "100%", sm: "auto" }, // Full width on small screens
            alignSelf: "center",
            border: "1px solid #183059"
          }}
        />

        {/* Stop ID Section */}
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
          mb: { xs: 2, sm: 0 } // Bottom margin only on small screens
        }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059"
              }}
            >
              Stop ID
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "18px", md: "24px" }, // Responsive font size
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center"
            }}
          >
            {stopData.stopId}
          </Typography>
        </Box>

        {/* Second divider */}
        <Divider
          orientation={{ xs: "horizontal", sm: "vertical" }}
          flexItem
          sx={{
            mx: { sm: 2 },
            my: { xs: 2, sm: 0 }, // Vertical margin on small screens
            height: { xs: "1px", sm: "60px" }, // Height for horizontal vs vertical
            width: { xs: "100%", sm: "auto" }, // Full width on small screens
            alignSelf: "center",
            border: "1px solid #183059"
          }}
        />

        {/* Connected Routes Section */}
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
          mb: { xs: 2, sm: 0 } // Bottom margin only on small screens
        }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059"
              }}
            >
              Connected Routes
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="body1"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "18px", md: "24px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center"
              }}
            >
              {stopData.routes.length}
            </Typography>
          </Box>
        </Box>

        {/* Third divider */}
        <Divider
          orientation={{ xs: "horizontal", sm: "vertical" }}
          flexItem
          sx={{
            mx: { sm: 2 },
            my: { xs: 2, sm: 0 }, // Vertical margin on small screens
            height: { xs: "1px", sm: "60px" }, // Height for horizontal vs vertical
            width: { xs: "100%", sm: "auto" }, // Full width on small screens
            alignSelf: "center",
            border: "1px solid #183059"
          }}
        />

        {/* Vehicles Stop Here Section */}
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1
        }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "16px", md: "20px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#183059"
              }}
            >
              Vehicles Stop Here
            </Typography>
          </Box>
          <Box
            sx={{
              padding: { xs: "4px 12px", md: "4px 16px" },
              borderRadius: "4px",
              display: "inline-block",
              textAlign: "center"
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "18px", md: "24px" }, // Responsive font size
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center"
              }}
            >
              0
              {/* {stopData || "---"} */}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* {renderRouteTable()} */}
      {hasMapPermission && renderMap()}
      {renderTable()}

    </>
  );
};

export default AdminStopView;